import React, { ReactElement } from 'react'
import styles from '@/components/ExamChangeGuidePage/ExamChangeInfo/ExamChangeInfoSharedStyling.module.scss'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const Spring2025: React.FC = (): ReactElement => {
    return (
        <div
            className={styles.examChangeGuideSharedComponent}
            id={'spring2025'}
        >
            <div className={styles.examChangeGuidePageTop}>
                <h2>Spring 2025</h2>
                <h3>Summary</h3>
                <div>
                    The SOA has released changes effective for the Spring 2025
                    sitting of FSA Group and Health Exams. The updated syllabi
                    can be found here:
                    <ul>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/4abfd4/globalassets/assets/files/edu/2025/spring/syllabi/ghdp.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Design and Pricing
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/49955d/globalassets/assets/files/edu/2025/spring/syllabi/ghvru.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Valuation and Regulation
                            </a>{' '}
                            (Unchanged from Fall 2024)
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/49964f/globalassets/assets/files/edu/2025/spring/syllabi/ghrm.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                Risk Mitigation
                            </a>
                        </li>
                    </ul>
                    Overall, there were only minor changes to the syllabi, as
                    there will be significant changes in the next sitting with
                    the Fall 2025 restructuring. The general trend is that there
                    was a handful of added and removed readings, but overall no
                    major shift in the total amount of content / size of
                    syllabus. Find the details on the changes below.
                    <br />
                    <br />
                    GHDP has the following changes:
                    <ul>
                        <li>
                            <b>Added</b> - Insuring LTC, Chapter 4: Hybrid Life
                            and Long-Term Care Products. A fairly short chapter.
                        </li>
                        <li>
                            <b>Added</b> - GHDP 146-25: A Hard Pill to Swallow:
                            Appreciating the Mathematical Dynamics of the
                            Affordable Care Act. This material was previously
                            part of the GHVR syllabus until it was removed in
                            Fall 2024. This section was tested only once in the
                            past (Q6c of the GHFVCU Fall 2021 sitting).
                        </li>
                        <li>
                            <b>Added</b> - GHDP 147-25: Attempting to Boil the
                            Ocean: A High-Level Overview of Medicaid and its
                            Risk-Based Managed Care Programs. This is a fairly
                            average sized section, but also includes a companion
                            Excel file, making it likely there will be math
                            problems asked from it.
                        </li>
                        <li>
                            <b>Added</b> -{' '}
                            <a
                                href={
                                    'https://www.cia-ica.ca/app/themes/wicket/custom/dl_file.php?p=35655&fid=15713'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Group Life Waiver Study
                            </a>{' '}
                            is a relatively short reading about a Canadian LTD
                            experience study.
                        </li>
                        <li>
                            <b>Removed</b> - GHDP 142-23: Medicaid A Primer was
                            removed from the syllabus. This section had only
                            been tested once in the past (Q5 of Spring 2021
                            GHFVCU).
                        </li>
                        <li>
                            <b>Removed</b> -{' '}
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/library/newsletters/product-development-news/2019/july/pro-2019-iss113-eaton.pdf'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Combination Products
                            </a>{' '}
                            was removed from the syllabus. This section had only
                            been tested once in the past (Q3e of Spring 2021
                            GHDP-C).
                        </li>
                        <li>
                            <b>Removed</b> - GHDP-101-13: Group Disability
                            Insurance (sections 4 & 7) was removed from the
                            syllabus. This section had been tested three times
                            in the past, the most recent of which being Q13a+b
                            of Fall 2022 GHDP.
                        </li>
                        <li>
                            <b>Removed</b> - GHDP-127-19: Chapters 16 and 19 of
                            Disability Income Insurance: The Unique Risk was
                            removed from the syllabus. This section had only
                            been tested once in the past (Q13c+d of Fall 2022
                            GHDP).
                        </li>
                        <li>
                            <b>Removed</b> -{' '}
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/library/newsletters/health-watch-newsletter/2008/may/hsn-2008-iss58.pdf'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Timing&apos;s Everything
                            </a>{' '}
                            was removed from the syllabus. This section had been
                            tested three times in the past, the most recent of
                            which being Q8g of Fall 2023 GHDP.
                        </li>
                        <li>
                            <b>Removed</b> -{' '}
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/resources/experience-studies/2019/2019-gltd-study-report.pdf'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                2019 Group LTD Disability Experience Study
                            </a>{' '}
                            was removed from the syllabus. This section had been
                            tested only once in the past (Q4d of Fall 2023
                            GHDP).
                        </li>
                    </ul>
                    <p>GHVR has no syllabus changes.</p>
                    GHRM has the following changes:
                    <ul>
                        <li>
                            <b>Added</b> -{' '}
                            <a
                                href={
                                    'https://www.soa.org/sections/health/health-newsletter/2024/september/hw-2024-09-bohn/'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Surprises in the No Surprises Act: An Interview
                                with Greg Fann
                            </a>
                            . This a fairly short, but insightful article about
                            the No Surprises Act.
                        </li>
                        <li>
                            <b>Added</b> -{' '}
                            <a
                                href={
                                    'https://www.soa.org/sections/health/health-newsletter/2024/november/hw-2024-11-bohn/'
                                }
                                className={
                                    reusableCssClass.clickableTextUnderlined
                                }
                            >
                                Surprises in the No Surprises Act: An Interview
                                with Greg Fann (Part 2)
                            </a>
                            . This is simply the second half of the interview of
                            the preceding addition to the syllabus.
                        </li>
                        <li>
                            <b>Added</b> - GHRM 116-25: HHS-Operated Risk
                            Adjustment Technical Paper on Possible Model
                            Changes. This is a relatively longer study note.
                        </li>
                        <li>
                            <b>Removed</b> - GHRM 112-23: HHS-Operated Risk
                            Adjustment Methodology Meeting Discussion Paper, Ch.
                            4. This section had been tested four times in the
                            past, the most recent of which being Q1a+b of Spring
                            2024 GHRM.
                        </li>
                        <li>
                            <b>Trivial Renaming</b> - Handbook of Employee
                            Benefits, Chapters 2 and were changed to a study
                            note and renamed to GHRM 115-24.
                        </li>
                        <li>
                            <b>Small Section Removed</b> - Healthcare Risk
                            Adjustment and Predictive Modeling, Chapter 21: Risk
                            Adjustment on the ACA Exchanges. Section 21.8.2 has
                            now been removed from the syllabus. The section
                            removed was fairly short, and discusses issues in
                            ACA Risk Adjustment, with separate sections for
                            Massachusetts and the National Risk Adjustment
                            programs.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    )
}
